import { Organism } from './organism'
import { Food } from './food'
import { SpatialGrid } from './spatial-grid'
import { DietType, type Obstacle, type PopSnapshot, type PopulationConfig } from './types'

const INITIAL_FOOD = 70
const MAX_FOOD = 180               // 食料上限を調整し、草食の安定個体数を確保
const FOOD_SPAWN_INTERVAL = 0.35   // 秒（獲物密度を維持して肉食の狩りを成立させる）
const MIN_ORGANISMS = 15
const OBSTACLE_COUNT = 6
/** 基準繁殖エネルギー（全食性で繁殖サイクルを緩やかに） */
const BASE_REPRODUCTION_ENERGY = 140
const REPRODUCTION_COST = 70
/** 世代記録・グラフ更新の間隔（tick単位） */
const SNAPSHOT_INTERVAL = 60
/** 保存するスナップショット数 */
const MAX_SNAPSHOTS = 300

/** SpatialGrid のセルサイズ（最大視野範囲と同程度） */
const GRID_CELL_SIZE = 160

export class World {
  width: number
  height: number
  organisms: Organism[]
  foods: Food[]
  obstacles: Obstacle[]
  grid: SpatialGrid

  private foodTimer = 0
  tick = 0
  maxGeneration = 0

  /** 個体数の時系列履歴 */
  popHistory: PopSnapshot[] = []
  private snapshotTimer = 0

  constructor(width: number, height: number, popConfig?: PopulationConfig) {
    this.width = width
    this.height = height
    this.organisms = []
    this.foods = []
    this.obstacles = []
    this.grid = new SpatialGrid(width, height, GRID_CELL_SIZE)
    this.init(popConfig)
  }

  private init(popConfig?: PopulationConfig): void {
    for (let i = 0; i < OBSTACLE_COUNT; i++) {
      this.obstacles.push({
        pos: {
          x: (0.1 + Math.random() * 0.8) * this.width,
          y: (0.1 + Math.random() * 0.8) * this.height,
        },
        radius: 20 + Math.random() * 25,
      })
    }

    if (popConfig) {
      this.spawnByDiet(DietType.Herbivore, popConfig.herbivores)
      this.spawnByDiet(DietType.Carnivore, popConfig.carnivores)
      this.spawnByDiet(DietType.Omnivore, popConfig.omnivores)
    } else {
      // デフォルト: 120体をランダム配分
      for (let i = 0; i < 120; i++) {
        const pos = this.safePosition()
        this.organisms.push(new Organism(pos.x, pos.y))
      }
    }

    for (let i = 0; i < INITIAL_FOOD; i++) {
      this.spawnFood()
    }
  }

  private spawnByDiet(diet: DietType, count: number): void {
    for (let i = 0; i < count; i++) {
      const pos = this.safePosition()
      const org = new Organism(pos.x, pos.y)
      org.traits.diet = diet
      // 食性に合わせた見た目と能力を設定
      if (diet === DietType.Herbivore) {
        org.traits.hue = 90 + Math.random() * 60
        org.traits.speed = 1.05 + Math.random() * 0.9
        org.traits.size = 3 + Math.random() * 3
      } else if (diet === DietType.Carnivore) {
        org.traits.hue = Math.random() * 30
        org.traits.speed = 1.75 + Math.random() * 1.5
        org.traits.size = 5 + Math.random() * 4
      } else {
        org.traits.hue = 200 + Math.random() * 80
        org.traits.speed = 1.4 + Math.random() * 1.2
        org.traits.size = 3 + Math.random() * 3
        org.traits.fertilityRate = 0.6 + Math.random() * 0.3
      }
      this.organisms.push(org)
    }
  }

  private safePosition(): { x: number; y: number } {
    for (let attempt = 0; attempt < 20; attempt++) {
      const x = Math.random() * this.width
      const y = Math.random() * this.height
      let ok = true
      for (const obs of this.obstacles) {
        const dx = x - obs.pos.x
        const dy = y - obs.pos.y
        if (Math.sqrt(dx * dx + dy * dy) < obs.radius + 10) { ok = false; break }
      }
      if (ok) return { x, y }
    }
    return { x: Math.random() * this.width, y: Math.random() * this.height }
  }

  update(dt: number): void {
    this.tick++

    // 空間グリッドを再構築（近傍探索の高速化）
    this.grid.rebuild(this.organisms)

    for (const org of this.organisms) {
      if (org.alive) {
        org.update(dt, this.foods, this.organisms, this.width, this.height, this.obstacles, this.grid)
      }
    }

    this.organisms = this.organisms.filter(o => o.alive)
    this.foods = this.foods.filter(f => f.alive)

    // 繁殖（個体ごとの fertilityRate で閾値が変わる）
    const toAdd: Organism[] = []
    for (const org of this.organisms) {
      const threshold = BASE_REPRODUCTION_ENERGY / org.traits.fertilityRate
      if (org.energy >= threshold) {
        // 食性別繁殖コスト（肉食は高コスト→少数精鋭、雑食は中間的なコスト）
        const reproductionCost = org.traits.diet === DietType.Herbivore
          ? REPRODUCTION_COST * 0.9   // 草食: コスト63
          : org.traits.diet === DietType.Omnivore
            ? REPRODUCTION_COST * 1.0  // 雑食: コスト70（基準値）
            : REPRODUCTION_COST * 1.5  // 肉食: コスト105（繁殖が高コスト）
        org.energy -= reproductionCost
        const offspring = this.reproduce(org)
        toAdd.push(offspring)
        if (offspring.generation > this.maxGeneration) {
          this.maxGeneration = offspring.generation
        }
      }
    }
    this.organisms.push(...toAdd)

    // 個体数が少ない場合の補充（草食を優先的に追加してエコシステムを維持）
    if (this.organisms.length < MIN_ORGANISMS) {
      const herbCount = this.organisms.filter(o => o.traits.diet === DietType.Herbivore).length
      for (let i = 0; i < 6; i++) {
        const pos = this.safePosition()
        const org = new Organism(pos.x, pos.y)
        // 草食が少ない場合は強制的に草食を追加
        if (herbCount + i < 3) {
          org.traits.diet = DietType.Herbivore
          org.traits.hue = 90 + Math.random() * 60
          org.traits.speed = 1.0 + Math.random() * 0.8
          org.traits.size = 3 + Math.random() * 2
        }
        this.organisms.push(org)
      }
    }

    // 草食の絶滅防止（最小限の介入: 完全絶滅時のみ少数補充）
    const herbivores = this.organisms.filter(o => o.traits.diet === DietType.Herbivore).length
    if (herbivores < 2 && this.tick % 180 === 0) {  // 3秒間隔、2体未満で発動
      for (let i = 0; i < 2; i++) {
        const pos = this.safePosition()
        const org = new Organism(pos.x, pos.y)
        org.traits.diet = DietType.Herbivore
        org.traits.hue = 90 + Math.random() * 60
        org.traits.speed = 1.2 + Math.random() * 0.8
        org.traits.size = 3 + Math.random() * 2
        this.organisms.push(org)
      }
    }

    // 食料スポーン
    this.foodTimer += dt
    if (this.foodTimer >= FOOD_SPAWN_INTERVAL) {
      this.foodTimer = 0
      if (this.foods.length < MAX_FOOD) {
        const n = Math.floor(Math.random() * 5) + 3  // 3-7個スポーン（草食有利すぎ修正）
        for (let i = 0; i < n; i++) this.spawnFood()
      }
    }

    // 個体数スナップショット（グラフ用）
    this.snapshotTimer++
    if (this.snapshotTimer >= SNAPSHOT_INTERVAL) {
      this.snapshotTimer = 0
      const snap = this.currentSnapshot()
      this.popHistory.push(snap)
      if (this.popHistory.length > MAX_SNAPSHOTS) {
        this.popHistory.shift()
      }
    }
  }

  private currentSnapshot(): PopSnapshot {
    let herbivores = 0
    let carnivores = 0
    let omnivores = 0
    for (const o of this.organisms) {
      if (o.traits.diet === DietType.Herbivore) herbivores++
      else if (o.traits.diet === DietType.Carnivore) carnivores++
      else omnivores++
    }
    return { tick: this.tick, herbivores, carnivores, omnivores }
  }

  private reproduce(parent: Organism): Organism {
    const pt = parent.traits
    const mutate = (v: number, range: number) => v + (Math.random() - 0.5) * range
    return new Organism(
      parent.pos.x + (Math.random() - 0.5) * 20,
      parent.pos.y + (Math.random() - 0.5) * 20,
      {
        speed: Math.max(0.3, mutate(pt.speed, 0.4)),
        visionRange: Math.max(20, mutate(pt.visionRange, 10)),
        size: Math.max(2, mutate(pt.size, 1)),
        diet: pt.diet,
        hue: (pt.hue + (Math.random() - 0.5) * 20 + 360) % 360,
        metabolismRate: Math.max(0.5, mutate(pt.metabolismRate, 0.15)),
        fertilityRate: Math.max(0.4, Math.min(2.0, mutate(pt.fertilityRate, 0.1))),
      },
      parent.generation + 1,
      parent.id,
    )
  }

  private spawnFood(): void {
    const pos = this.safePosition()
    this.foods.push(new Food(pos.x, pos.y, 20 + Math.random() * 20))
  }

  get stats() {
    const snap = this.currentSnapshot()
    return {
      organisms: this.organisms.length,
      foods: this.foods.length,
      tick: this.tick,
      herbivores: snap.herbivores,
      carnivores: snap.carnivores,
      omnivores: snap.omnivores,
      maxGeneration: this.maxGeneration,
    }
  }
}
